import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';

const QuestSystemPage = () => {
  const navigate = useNavigate();
  const { user, updateMeritPoints } = useUser();
  
  const [activeTab, setActiveTab] = useState('available');
  const [loading, setLoading] = useState(true);
  const [quests, setQuests] = useState([]);
  const [selectedQuest, setSelectedQuest] = useState(null);
  
  // 如果用户未登录，重定向到登录页面
  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      // 加载任务数据
      loadQuests();
    }
  }, [user, navigate]);
  
  // 加载任务数据（模拟）
  const loadQuests = () => {
    setTimeout(() => {
      const mockQuests = [
        {
          id: 1,
          title: '每日冥想',
          description: '完成15分钟冥想，稳定心神，增强修为。',
          type: 'daily',
          difficulty: 'easy',
          reward: 10,
          status: 'available',
          requirements: ['筑基期及以上'],
          steps: ['打开冥想模块', '选择基础冥想', '完成15分钟冥想'],
          location: '线上'
        },
        {
          id: 2,
          title: '筑基丹炼制',
          description: '学习炼制筑基丹的基本步骤，是丹药入门的基础。',
          type: 'learning',
          difficulty: 'medium',
          reward: 20,
          status: 'available',
          requirements: ['炼气期及以上', '丹药学入门'],
          steps: ['准备炼丹材料', '学习基础炼丹术', '完成筑基丹炼制'],
          location: '线下道场'
        },
        {
          id: 3,
          title: '道友互助',
          description: '帮助3位道友解决修炼问题，促进宗门交流。',
          type: 'social',
          difficulty: 'medium',
          reward: 15,
          status: 'available',
          requirements: ['筑基期及以上'],
          steps: ['在论坛回答问题', '参与线下交流会', '记录互助内容'],
          location: '线上/线下'
        },
        {
          id: 4,
          title: '灵气感知',
          description: '完成基础灵气感知训练，提高对周围灵气的敏感度。',
          type: 'training',
          difficulty: 'easy',
          reward: 10,
          status: 'in-progress',
          requirements: ['无'],
          steps: ['学习感知技巧', '进行感知练习', '完成灵气定位'],
          location: '线上'
        }
      ];
      
      setQuests(mockQuests);
      setLoading(false);
    }, 1000);
  };
  
  // 接受任务
  const acceptQuest = (quest) => {
    const updatedQuests = quests.map(q => 
      q.id === quest.id ? { ...q, status: 'in-progress' } : q
    );
    setQuests(updatedQuests);
    setSelectedQuest({ ...quest, status: 'in-progress' });
  };
  
  // 完成任务
  const completeQuest = (quest) => {
    const updatedQuests = quests.map(q => 
      q.id === quest.id ? { ...q, status: 'completed' } : q
    );
    setQuests(updatedQuests);
    setSelectedQuest({ ...quest, status: 'completed' });
    
    // 增加功德点奖励
    updateMeritPoints(quest.reward);
  };
  
  // 任务状态样式
  const getStatusStyle = (status) => {
    switch (status) {
      case 'available':
        return 'text-cyber-primary';
      case 'in-progress':
        return 'text-yellow-400';
      case 'completed':
        return 'text-green-400';
      default:
        return 'text-cyber-light';
    }
  };
  
  // 任务难度映射
  const difficultyMap = {
    'easy': '简单',
    'medium': '中等',
    'hard': '困难'
  };
  
  // 过滤任务
  const filterQuests = () => {
    switch(activeTab) {
      case 'available':
        return quests.filter(quest => quest.status === 'available');
      case 'in-progress':
        return quests.filter(quest => quest.status === 'in-progress');
      case 'completed':
        return quests.filter(quest => quest.status === 'completed');
      default:
        return quests;
    }
  };
  
  return (
    <div className="space-y-8">
      <section className="cyber-panel p-6 rounded-lg bg-gradient-to-r from-cyber-dark to-black">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div>
            <h1 className="text-3xl font-xianxia text-cyber-primary mb-2 cyber-title">修真任务系统</h1>
            <p className="text-cyber-light">
              完成任务获取功德点和修为，提升境界
            </p>
          </div>
        </div>
      </section>
      
      {/* 标签导航 */}
      <div className="border-b border-cyber-primary">
        <nav className="flex flex-wrap -mb-px">
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'available'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('available')}
          >
            可接任务
          </button>
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'in-progress'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('in-progress')}
          >
            进行中
          </button>
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'completed'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('completed')}
          >
            已完成
          </button>
        </nav>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* 任务列表 */}
        <div className="md:col-span-1">
          <div className="cyber-panel p-4 rounded-lg">
            <h2 className="text-xl font-cyber text-cyber-primary mb-4">
              任务列表
            </h2>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="text-cyber-primary text-xl mb-2">
                  <i className="fas fa-spinner fa-spin"></i>
                </div>
                <p className="text-cyber-light">加载任务中...</p>
              </div>
            ) : filterQuests().length === 0 ? (
              <div className="text-center py-8">
                <div className="text-cyber-accent text-3xl mb-2">
                  <i className="fas fa-scroll"></i>
                </div>
                <p className="text-cyber-light">暂无任务</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filterQuests().map(quest => (
                  <div 
                    key={quest.id}
                    className={`cyber-panel p-3 rounded cursor-pointer transition-all duration-300 ${
                      selectedQuest && selectedQuest.id === quest.id
                        ? 'border-cyber-primary bg-cyber-primary bg-opacity-10'
                        : 'hover:border-cyber-secondary'
                    }`}
                    onClick={() => setSelectedQuest(quest)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-cyber-secondary">{quest.title}</h3>
                        <div className="flex items-center mt-1">
                          <span className="text-xs text-cyber-light mr-3">
                            {difficultyMap[quest.difficulty]}
                          </span>
                          <span className="text-xs text-cyber-light">
                            奖励: {quest.reward} 点
                          </span>
                        </div>
                      </div>
                      <div className={`text-xs ${getStatusStyle(quest.status)}`}>
                        {quest.status === 'available' && '可接取'}
                        {quest.status === 'in-progress' && '进行中'}
                        {quest.status === 'completed' && '已完成'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* 任务详情 */}
        <div className="md:col-span-2">
          {selectedQuest ? (
            <div className="cyber-panel p-6 rounded-lg">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-cyber text-cyber-primary">
                  {selectedQuest.title}
                </h2>
                <div className={`px-3 py-1 rounded-full text-xs ${getStatusStyle(selectedQuest.status)} border border-current`}>
                  {selectedQuest.status === 'available' && '可接取'}
                  {selectedQuest.status === 'in-progress' && '进行中'}
                  {selectedQuest.status === 'completed' && '已完成'}
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg text-cyber-secondary mb-2">任务描述</h3>
                  <p className="text-cyber-light">{selectedQuest.description}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="cyber-panel p-3 rounded">
                    <div className="text-sm text-cyber-light mb-1">类型</div>
                    <div className="text-cyber-secondary">
                      {selectedQuest.type === 'daily' && '日常任务'}
                      {selectedQuest.type === 'learning' && '学习任务'}
                      {selectedQuest.type === 'social' && '社交任务'}
                      {selectedQuest.type === 'training' && '训练任务'}
                    </div>
                  </div>
                  
                  <div className="cyber-panel p-3 rounded">
                    <div className="text-sm text-cyber-light mb-1">难度</div>
                    <div className="text-cyber-secondary">
                      {difficultyMap[selectedQuest.difficulty]}
                    </div>
                  </div>
                  
                  <div className="cyber-panel p-3 rounded">
                    <div className="text-sm text-cyber-light mb-1">奖励</div>
                    <div className="text-cyber-accent">
                      {selectedQuest.reward} 功德点
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg text-cyber-secondary mb-2">要求</h3>
                  <ul className="cyber-panel p-3 rounded space-y-1">
                    {selectedQuest.requirements.map((req, index) => (
                      <li key={index} className="text-cyber-light">
                        • {req}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg text-cyber-secondary mb-2">任务步骤</h3>
                  <ol className="cyber-panel p-3 rounded space-y-3">
                    {selectedQuest.steps.map((step, index) => (
                      <li key={index} className="flex items-start">
                        <div className="w-6 h-6 rounded-full bg-cyber-dark border border-cyber-primary flex items-center justify-center mr-3 flex-shrink-0">
                          <span className="text-cyber-primary text-xs">{index + 1}</span>
                        </div>
                        <div className="text-cyber-light pt-1">{step}</div>
                      </li>
                    ))}
                  </ol>
                </div>
                
                <div>
                  <h3 className="text-lg text-cyber-secondary mb-2">任务地点</h3>
                  <div className="cyber-panel p-3 rounded">
                    <div className="flex items-center">
                      <i className="fas fa-map-marker-alt text-cyber-primary mr-2"></i>
                      <span className="text-cyber-light">{selectedQuest.location}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-center pt-4">
                  {selectedQuest.status === 'available' && (
                    <button
                      className="cyber-button px-8 py-3"
                      onClick={() => acceptQuest(selectedQuest)}
                    >
                      接受任务
                    </button>
                  )}
                  
                  {selectedQuest.status === 'in-progress' && (
                    <button
                      className="cyber-button px-8 py-3"
                      onClick={() => completeQuest(selectedQuest)}
                    >
                      完成任务
                    </button>
                  )}
                  
                  {selectedQuest.status === 'completed' && (
                    <div className="cyber-panel p-3 rounded text-center border border-green-500">
                      <div className="text-green-400 mb-1">
                        <i className="fas fa-check-circle mr-2"></i>
                        已完成
                      </div>
                      <div className="text-cyber-light text-sm">
                        你已获得 {selectedQuest.reward} 功德点奖励
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="cyber-panel p-6 rounded-lg h-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-cyber-accent text-5xl mb-4">
                  <i className="fas fa-scroll"></i>
                </div>
                <p className="text-cyber-light">请从左侧选择一个任务查看详情</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestSystemPage;
